import type { Invitation, Membership, Role, User } from "../models";
import {
  SEED_IDENTITY_USERS,
  SEED_IDENTITY_ROLES,
  SEED_MEMBERSHIPS,
  SEED_INVITATIONS,
  DEMO_EMAIL_ROLE_MAP,
} from "./seed";
import {
  membershipUniqueKey,
  normalizeMemberships,
} from "../utils/membership.utils";

const USE_API = import.meta.env.VITE_ITAAS_USE_IDENTITY_API === "true";
const PERSIST_KEY = "itaas:identity-store:v1";
const SEED_USER_IDS = new Set(SEED_IDENTITY_USERS.map((u) => u.id));
const SEED_MEMBERSHIP_BY_KEY = new Map(
  SEED_MEMBERSHIPS.map((m) => [
    membershipUniqueKey(m.userId, m.tenantId, m.roleId),
    m,
  ])
);

interface PersistedIdentitySlice {
  users: User[];
  memberships: Membership[];
  emailRoleCodes?: Record<string, string>;
}

function cloneSeedData() {
  return {
    users: structuredClone(SEED_IDENTITY_USERS),
    roles: structuredClone(SEED_IDENTITY_ROLES),
    memberships: normalizeMemberships(structuredClone(SEED_MEMBERSHIPS)),
    invitations: structuredClone(SEED_INVITATIONS),
    emailRoleCodes: { ...DEMO_EMAIL_ROLE_MAP } as Record<string, string>,
  };
}

function loadPersistedSlice(): PersistedIdentitySlice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedIdentitySlice;
    if (!Array.isArray(parsed.users) || !Array.isArray(parsed.memberships)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePersistedSlice(data: ReturnType<typeof cloneSeedData>): void {
  if (typeof window === "undefined") return;
  const membershipsToPersist = data.memberships.filter((m) => {
    const seed = SEED_MEMBERSHIP_BY_KEY.get(
      membershipUniqueKey(m.userId, m.tenantId, m.roleId)
    );
    if (!seed) return true;
    return seed.status !== m.status;
  });
  const emailRoleCodes: Record<string, string> = {};
  for (const [email, code] of Object.entries(data.emailRoleCodes)) {
    const seedCode = DEMO_EMAIL_ROLE_MAP[email];
    if (!seedCode || seedCode !== code) {
      emailRoleCodes[email] = code;
    }
  }
  const slice: PersistedIdentitySlice = {
    users: data.users.filter((u) => !SEED_USER_IDS.has(u.id)),
    memberships: membershipsToPersist,
    emailRoleCodes,
  };
  window.localStorage.setItem(PERSIST_KEY, JSON.stringify(slice));
}

function hydrateStore(): ReturnType<typeof cloneSeedData> {
  const seed = cloneSeedData();
  const persisted = loadPersistedSlice();
  if (!persisted) return seed;

  const userIds = new Set(seed.users.map((u) => u.id));
  for (const user of persisted.users) {
    if (!userIds.has(user.id)) {
      seed.users.push(user);
      userIds.add(user.id);
    }
  }

  const membershipByKey = new Map(
    seed.memberships.map((m) => [
      membershipUniqueKey(m.userId, m.tenantId, m.roleId),
      m,
    ])
  );
  for (const membership of persisted.memberships) {
    membershipByKey.set(
      membershipUniqueKey(
        membership.userId,
        membership.tenantId,
        membership.roleId
      ),
      membership
    );
  }
  seed.memberships = normalizeMemberships([...membershipByKey.values()]);

  if (persisted.emailRoleCodes) {
    seed.emailRoleCodes = {
      ...seed.emailRoleCodes,
      ...persisted.emailRoleCodes,
    };
  }

  return seed;
}

let store = hydrateStore();

export function initIdentityStore(): void {
  store = cloneSeedData();
  savePersistedSlice(store);
}

export function shouldUseIdentityApi(): boolean {
  return USE_API;
}

export function getStoreUsers(): User[] {
  return store.users;
}

export function getStoreRoles(): Role[] {
  return store.roles;
}

export function getStoreMemberships(): Membership[] {
  return store.memberships;
}

export function getStoreInvitations(): Invitation[] {
  return store.invitations;
}

/** Fallback legacy pour comptes sans membership identity. */
export function getEmailRoleCode(email: string): string | undefined {
  const normalized = email.trim().toLowerCase();
  return store.emailRoleCodes[normalized] ?? DEMO_EMAIL_ROLE_MAP[normalized];
}

export function mutateIdentityStore(
  mutator: (data: typeof store) => void
): void {
  mutator(store);
  store.memberships = normalizeMemberships(store.memberships);
  savePersistedSlice(store);
}
